import React, {
  useEffect,
  useRef,
  useState,
  useCallback
} from 'react';

import {
  Typography,
  Box,
  Stack,
  Button,
  LinearProgress
} from '@mui/material';

import GridLayout from '../components/layout/GridLayout';
import Menu from '../components/layout/Menu';
import Card from '../components/ui/Card';

interface Props {
  isMenuOpen?: boolean;
}

const CARD_DURATION = 1000 * 60 * 3;

export default function CardSequencesPage({
  isMenuOpen = false
}: Props) {

  const images = [
    '20.JPG',
    '7.JPG',
    '8.JPG',
    '9.JPG',
    '12.JPG',
    '13.JPG',
    '16.JPG',
    '19.JPG',
    '26.JPG',
    '23.JPG'
  ];

  const [currentIndex, setCurrentIndex] =
    useState(0);

  const [isPlaying, setIsPlaying] =
    useState(true);

  const [progress, setProgress] =
    useState(0);

  const [isMuted, setIsMuted] =
    useState(true);

  const startTimeRef =
    useRef<number>(Date.now());

  useEffect(() => {

    const saved =
      localStorage.getItem(
        'pilates_session'
      );

    if (saved) {
      const { index } = JSON.parse(saved);
      setCurrentIndex(index || 0);
    }

    const muteSaved =
      localStorage.getItem('mute');

    if (muteSaved) {
      setIsMuted(JSON.parse(muteSaved));
    }

  }, []);

  useEffect(() => {

    localStorage.setItem(
      'pilates_session',
      JSON.stringify({
        index: currentIndex
      })
    );

  }, [currentIndex]);

  useEffect(() => {

    localStorage.setItem(
      'mute',
      JSON.stringify(isMuted)
    );

  }, [isMuted]);

  const speak = useCallback(
    (text: string) => {

      if (isMuted) return;

      const utterance =
        new SpeechSynthesisUtterance(text);

      utterance.rate = 0.9;

      speechSynthesis.cancel();

      speechSynthesis.speak(utterance);

    },
    [isMuted]
  );

  const nextCard = useCallback(() => {

    setProgress(0);

    setCurrentIndex((prev) =>
      (prev + 1) % images.length
    );

  }, [images.length]);

  const prevCard = () => {

    setProgress(0);

    setCurrentIndex((prev) =>
      (prev - 1 + images.length) %
      images.length
    );
  };

  useEffect(() => {

    if (!isPlaying) return;

    startTimeRef.current = Date.now();

    const interval = setInterval(() => {

      const elapsed =
        Date.now() - startTimeRef.current;

      const percent =
        (elapsed / CARD_DURATION) * 100;

      if (percent >= 100) {

        nextCard();

      } else {

        setProgress(percent);

      }

    }, 100);

    return () => clearInterval(interval);

  }, [currentIndex, isPlaying, nextCard]);

  useEffect(() => {

    if (!isMuted) {

      speak(
        `Step ${currentIndex + 1}. Hold position. Breathe slowly.`
      );

    }

  }, [currentIndex, isMuted, speak]);

  const togglePlay = () => {
    setIsPlaying((prev) => !prev);
  };

  const toggleMute = () => {

    setIsMuted((prev) => {

      const next = !prev;

      if (next) {

        speechSynthesis.cancel();

      } else {

        speak(
          `Step ${currentIndex + 1}. Hold position.`
        );

      }

      return next;

    });

  };

  let touchStartX = 0;

  const handleTouchStart = (
    e: React.TouchEvent
  ) => {

    touchStartX =
      e.touches[0].clientX;

  };

  const handleTouchEnd = (
    e: React.TouchEvent
  ) => {

    const diff =
      touchStartX -
      e.changedTouches[0].clientX;

    if (diff > 50) nextCard();

    if (diff < -50) prevCard();

  };

  return (
    <GridLayout
      isMenuOpen={isMenuOpen}
      left={<Menu />}

      main={
        <Stack spacing={3}>

          <Typography
            variant="h4"
            sx={{
              color: '#34de9aab',
              fontWeight: 'bold'
            }}
          >
            30min Rehabilitation Flow
          </Typography>

          <Card
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            sx={{
              textAlign: 'center'
            }}
          >
            <Box
              component="img"
              src={`/images/${images[currentIndex]}`}
              sx={{
                width: '90%',
                height: 500,
                objectFit: 'cover',
                borderRadius: 3
              }}
            />

            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 6,
                borderRadius: 3,
                mt: 1,
                backgroundColor: '#eee',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#34de9aab'
                }
              }}
            />

            <Typography
              sx={{
                mt: 2,
                fontWeight: 'bold'
              }}
            >
              Step {currentIndex + 1}
              {' / '}
              {images.length}
            </Typography>

            <Typography
              variant="body2"
              color="#34de9aab"
            >
              {isPlaying
                ? 'Follow and breathe, relax and massage with pics'
                : 'Paused'}
            </Typography>

            <Stack
              direction="row"
              spacing={2}
              sx={{
                justifyContent: 'center',
                mt: 2
              }}
            >
              <Button onClick={prevCard}>
                Prev
              </Button>

              <Button
                variant="contained"
                onClick={togglePlay}
                sx={{
                  bgcolor: '#34de9aab'
                }}
              >
                {isPlaying
                  ? 'Pause'
                  : 'Play'}
              </Button>

              <Button onClick={nextCard}>
                Next
              </Button>

              <Button
                variant="outlined"
                onClick={toggleMute}
              >
                {isMuted
                  ? 'Unmute'
                  : 'Mute'}
              </Button>
            </Stack>
          </Card>

        </Stack>
      }

      right={
        <Card>
          <Typography
            variant="h6"
            sx={{
              color: '#34de9aab'
            }}
          >
            Smart Features
          </Typography>

          <Typography
            variant="body2"
            sx={{ mt: 1 }}
          >
            • AI voice guidance
            <br />
            • Swipe support
            <br />
            • Resume session
            <br />
            • Easy and Everyday
          </Typography>
        </Card>
      }
    />
  );
}